// frontend/src/pages/dashboard/TeamDetailsPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamMemberAPI, projectAPI, taskAPI, userAPI, sprintAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Typography, Box, Grid, Card, CardContent, CardHeader, CardActions,
  Avatar, Chip, IconButton, Tooltip, Paper, TextField,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  LinearProgress, Alert, Collapse, Tabs, Tab,
  List, ListItem, ListItemText, ListItemAvatar,
  InputAdornment, useTheme, useMediaQuery, Autocomplete, Skeleton
} from '@mui/material';
import {
  Search as SearchIcon, FilterList as FilterIcon, Person as PersonIcon,
  Timeline as TimelineIcon, TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon, CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, Close as CloseIcon, Visibility as VisibilityIcon,
  Email as EmailIcon, Warning as WarningIcon, Refresh as RefreshIcon, ViewKanban as ViewKanbanIcon
} from '@mui/icons-material';

// Memoized Member Card Component
const MemberCard = memo(({ member, stats, kpis, isExpanded, expandedMember, 
  toggleExpand, handleViewDetails, getMemberTasks, navigate, getMemberSprints, 
  getRoleColor, getAvatarUrl }) => {
  
  const roleName = member.role?.name || 'Member';
  const isCardExpanded = expandedMember === member.user_id;
  const userId = member.user_id;
  
  const recentTasks = useMemo(() => 
    getMemberTasks(userId).slice(0, 3),
    [userId, getMemberTasks]
  );

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Card  sx={{
        borderRadius: 2, 
        transition: 'all 0.2s',
        '&:hover': { 
          boxShadow: 4, 
          transform: 'translateY(-2px)' 
        },
        border: `2px solid ${getRoleColor(roleName)}`, 
        position: 'relative',
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
      }}>
        <Box sx={{
          position: 'absolute', 
          top: 12, 
          right: 12, 
          width: 12, 
          height: 12,
          borderRadius: '50%', 
          bgcolor: member.is_active ? 'success.main' : 'error.main',
          border: '2px solid white', 
          boxShadow: 1
        }} />
        
        <CardHeader
          onClick={() => handleViewDetails(member)}
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, cursor: 'pointer' }}
          avatar={
            <Avatar src={getAvatarUrl(member.user?.avatar)} sx={{
              width: 56, height: 56, bgcolor: getRoleColor(roleName), fontWeight: 600
            }}>
              {!member.user?.avatar ? member.user?.name?.charAt(0)?.toUpperCase() : null}
            </Avatar>
          }
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography component="span" fontWeight={600} variant="body1">
                {member.user?.name}
              </Typography>
              <Chip label={roleName} size="small" sx={{
                bgcolor: getRoleColor(roleName), color: 'white',
                fontSize: '0.7rem', height: 20
              }} />
            </Box>
          }
          subheader={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" noWrap>
                {member.user?.email}
              </Typography>
            </Box>
          }
        />
        
        <CardContent sx={{ pt: 0, flexGrow: 1 }}>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.lighter', borderRadius: 1 }}>
                <CheckCircleIcon fontSize="small" color="success" />
                <Typography variant="caption" display="block" fontWeight={600}>
                  {stats.completed}
                </Typography>
                <Typography variant="caption" color="text.secondary">Done</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                <HourglassIcon fontSize="small" color="warning" />
                <Typography variant="caption" display="block" fontWeight={600}>
                  {stats.inProgress}
                </Typography>
                <Typography variant="caption" color="text.secondary">In Progress</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}>
                <WarningIcon fontSize="small" color="error" />
                <Typography variant="caption" display="block" fontWeight={600}>
                  {stats.overdue}
                </Typography>
                <Typography variant="caption" color="text.secondary">Overdue</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <AccessTimeIcon fontSize="small" color="info" />
                <Typography variant="caption" display="block" fontWeight={600}>
                  {stats.totalTime}h
                </Typography>
                <Typography variant="caption" color="text.secondary">Tracked</Typography>
              </Box>
            </Grid>
          </Grid>

          {getMemberSprints(userId).length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.lighter', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} color="primary.main" 
                display="flex" alignItems="center" gap={0.5}>
                <TimelineIcon fontSize="small" /> Active Sprint
              </Typography>
              <Typography variant="body2" fontWeight={500} noWrap>
                {getMemberSprints(userId)[0]?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ends: {new Date(getMemberSprints(userId)[0]?.end_date).toLocaleDateString()}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={600} display="flex" 
              alignItems="center" gap={0.5}>
              <TrendingUpIcon fontSize="small" /> KPI Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={kpis.length > 0 ? (kpis.filter(k => k.status === 'approved').length / kpis.length * 100) : 0}
                sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
              />
              <Typography variant="caption">
                {kpis.filter(k => k.status === 'approved').length}/{kpis.length}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, mt: 'auto' }}>
          <Button size="small" onClick={() => toggleExpand(userId)}
            endIcon={isCardExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
            {isCardExpanded ? 'Less' : 'More'}
          </Button>
          <Button size="small" variant="contained" onClick={() => handleViewDetails(member)}
            sx={{ textTransform: 'none' }}>Full Profile</Button>
        </CardActions>

        <Collapse in={isCardExpanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Recent Tasks</Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
              {recentTasks.map(task => (
                <ListItem key={task.id} disableGutters sx={{ py: 0.5 }}>
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <Chip size="small" label={task.priority?.charAt(0).toUpperCase()}
                      color={task.priority === 'high' || task.priority === 'urgent' ? 'error' :
                        task.priority === 'low' ? 'success' : 'warning'}
                      sx={{ fontSize: '0.65rem', width: 24, height: 24 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}
                        onClick={() => navigate(`/tasks?project=${task.project_id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        noWrap>
                        {task.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary"
                          onClick={() => navigate(`/projects/${task.project_id}/board`)}
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          noWrap>
                          {task.project?.name}
                        </Typography>
                        {task.due_date && new Date(task.due_date) < new Date() && 
                         task.status?.key !== 'task_done' && (
                          <Chip label="Overdue" size="small" color="error" 
                            sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {recentTasks.length === 0 && (
                <Typography variant="caption" color="text.secondary">No tasks assigned</Typography>
              )}
            </List>
          </Box>
        </Collapse>
      </Card>
    </Grid>
  );
});

MemberCard.displayName = 'MemberCard';

// Main Component
const TeamDetailsPage = () => {
  const { user: authUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const searchInputRef = useRef(null);
  const isAdmin = authUser?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMember, setExpandedMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [memberStats, setMemberStats] = useState({});
  const [memberKpis, setMemberKpis] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
    project: 'all',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const projectsMap = useMemo(() => {
    const map = new Map();
    projects.forEach(p => map.set(p.id, p));
    return map;
  }, [projects]);

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [membersRes, projectsRes, tasksRes] = await Promise.all([
        teamMemberAPI.getAll(),
        projectAPI.getAll({ with: 'users' }),
        taskAPI.getAll()
      ]);

      const extractArray = (res) => {
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res)) return res;
        if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
        return [];
      };

      const membersData = extractArray(membersRes);
      const projectsData = extractArray(projectsRes);
      const tasksData = extractArray(tasksRes);

      setTeamMembers(membersData);
      setProjects(projectsData);
      setTasks(tasksData);

      const allSprints = [];
      const activeProjects = projectsData.filter(p => 
        p.status?.key !== 'completed' && p.status?.key !== 'cancelled'
      ).slice(0, 10);

      await Promise.all(activeProjects.map(async (project) => {
        try {
          const sprintRes = await sprintAPI.getAll(project.id);
          const sprintsData = extractArray(sprintRes);
          allSprints.push(...sprintsData.map(s => ({ ...s, projectName: project.name })));
        } catch (e) { /* ignore */ }
      }));

      setSprints(allSprints);
    } catch (err) {
      console.error('Failed to load team details:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  const fetchMemberKpis = useCallback(async (memberId) => {
    try {
      const res = await userAPI.getKpis(memberId);
      return res.data?.kpis?.approved || [];
    } catch {
      return [];
    }
  }, []);

  const memberTasksMap = useMemo(() => {
    const map = new Map();
    tasks.forEach(task => {
      const assignedUsers = task.assignedUsers || [];
      assignedUsers.forEach(user => {
        if (!map.has(user.id)) map.set(user.id, []);
        map.get(user.id).push(task);
      });
      if (task.created_by) {
        if (!map.has(task.created_by)) map.set(task.created_by, []);
        map.get(task.created_by).push(task);
      }
    });
    return map;
  }, [tasks]);

  const getMemberProjects = useCallback((userId) => {
    return projects.filter(p => {
      const assignedList = p.assignedUsers || p.assigned_user_ids || p.user_ids || [];
      const isAssigned = Array.isArray(assignedList) && assignedList.some(u => {
        const uid = typeof u === 'object' ? u?.id : u;
        return uid == userId;
      });
      const isCreator = p.created_by == userId;
      return isAssigned || isCreator;
    });
  }, [projects]);

  const getMemberTasks = useCallback((memberId) => {
    return memberTasksMap.get(memberId) || [];
  }, [memberTasksMap]);

  const getMemberSprints = useCallback((memberId) => {
    const memberProjects = getMemberProjects(memberId);
    const memberProjectIds = new Set(memberProjects.map(p => p.id));
    return sprints.filter(s => 
      memberProjectIds.has(s.project_id) && s.status === 'active'
    );
  }, [sprints, getMemberProjects]);

  const calculateMemberStats = useCallback((member) => {
    const userId = member.user_id;
    const memberTasks = getMemberTasks(userId);
    
    let completed = 0;
    let inProgress = 0;
    let overdue = 0;
    let totalTime = 0;
    const now = new Date();
    
    memberTasks.forEach(t => {
      if (t.status?.key === 'task_done') completed++;
      else if (t.status?.key === 'task_in_progress') inProgress++;
      
      if (t.status?.key !== 'task_done' && t.due_date && new Date(t.due_date) < now) {
        overdue++;
      }
      
      if (t.tracked_time?.total_seconds) {
        totalTime += Math.floor(t.tracked_time.total_seconds / 3600);
      }
    });

    return { completed, inProgress, overdue, totalTime, total: memberTasks.length };
  }, [getMemberTasks]);

  const filteredMembers = useMemo(() => {
    return teamMembers.filter(member => {
      const matchesSearch = !filters.search ||
        member.user?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        member.user?.email?.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesRole = filters.role === 'all' ||
        member.role?.name?.toLowerCase() === filters.role.toLowerCase();
      
      const matchesStatus = filters.status === 'all' ||
        (filters.status === 'active' ? member.is_active : !member.is_active);
      
      const matchesProject = filters.project === 'all' ||
        getMemberProjects(member.user_id).some(p => p.id == filters.project);
      
      return matchesSearch && matchesRole && matchesStatus && matchesProject;
    });
  }, [teamMembers, filters, getMemberProjects]);

  const projectOptions = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    const projectList = Array.isArray(projects) ? projects : (projects.data || []);
    return [...new Map(
      projectList.filter(p => p && p.id).map(p => [p.id, p])
    ).values()];
  }, [projects]);

  const roleOptions = useMemo(() => {
    return ['all', ...new Set(teamMembers.map(m => m.role?.name).filter(Boolean))];
  }, [teamMembers]);

  const resetFilters = useCallback(() => {
    setFilters({ search: '', role: 'all', status: 'all', project: 'all' });
  }, []);

  const renderFilterChips = useCallback(() => {
    const chips = [];
    if (filters.search) chips.push(`Search: "${filters.search}"`);
    if (filters.role !== 'all') chips.push(`Role: ${filters.role}`);
    if (filters.status !== 'all') chips.push(`Status: ${filters.status}`);
    if (filters.project !== 'all') {
      const proj = projectsMap.get(filters.project);
      chips.push(`Project: ${proj?.name || 'Unknown'}`);
    }
    return chips;
  }, [filters, projectsMap]);

  const handleViewDetails = useCallback(async (member) => {
    setSelectedMember(member);
    const kpis = await fetchMemberKpis(member.user_id);
    setMemberKpis(prev => ({ ...prev, [member.user_id]: kpis }));
    const stats = calculateMemberStats(member);
    setMemberStats(prev => ({ ...prev, [member.user_id]: stats }));
    setDetailDialogOpen(true);
  }, [fetchMemberKpis, calculateMemberStats]);

  const handleCloseDetails = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedMember(null);
  }, []);

  const toggleExpand = useCallback((memberId) => {
    setExpandedMember(prev => prev === memberId ? null : memberId);
  }, []);

  const getAvatarUrl = useCallback((avatarPath) => {
    if (!avatarPath) return null;
    const baseUrl = process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, '') || 'http://localhost:8000/storage';
    const cleanPath = avatarPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}?t=${Date.now()}`;
  }, []);

  const getRoleColor = useCallback((roleName) => {
    const colors = {
      'system administrator': '#dc2626',
      'manager': '#2563eb',
      'member': '#059669',
    };
    return colors[roleName?.toLowerCase()] || '#6b7280';
  }, []);

  const sharedButtonStyles = {
    height: 48, 
    px: 3, 
    borderRadius: 3, 
    fontWeight: 600,
    letterSpacing: '0.5px', 
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    width: isMobile ? "100%" : "auto",
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    '& .MuiButton-startIcon': {
      '& > *:nth-of-type(1)': { fontSize: isMobile ? 18 : 20 },
    },
  };

  const renderSkeletons = useCallback(() => (
    <Grid container spacing={{ xs: 2, sm: 3 }} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardHeader
              avatar={<Skeleton variant="circular" width={56} height={56} />}
              action={<Skeleton variant="circular" width={32} height={32} />}
              title={
                <Box>
                  <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                  <Skeleton variant="rounded" width={60} height={20} />
                </Box>
              }
              subheader={<Skeleton variant="text" width="80%" height={16} />}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[1, 2, 3, 4].map((stat) => (
                  <Grid size={{ xs: 6 }} key={stat}>
                    <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} sx={{ mx: 'auto', mb: 0.5 }} />
                      <Skeleton variant="text" width={30} height={20} sx={{ mx: 'auto' }} />
                      <Skeleton variant="text" width={50} height={14} sx={{ mx: 'auto' }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1 }}>
                <Skeleton variant="text" width="70%" height={16} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="80%" height={18} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="50%" height={14} />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Skeleton variant="text" width="40%" height={16} sx={{ mb: 0.5 }} />
                <Skeleton variant="rounded" width="100%" height={6} />
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
              <Skeleton variant="text" width={50} height={24} />
              <Skeleton variant="rounded" width={80} height={32} />
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  ), []);

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Admin access required. You cannot view team details.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ pt: 6, pl: 3, pr: 3, pb: 6, background: '#f5f7fa', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
          mb: 3, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <Box>
            <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={300} height={20} />
          </Box>
          <Skeleton variant="rounded" width={140} height={48} />
        </Box>
        <Paper elevation={0} sx={{ width: '100%', p: 2, mb: 3, borderRadius: 3, 
          bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={40} height={40} />
            <Skeleton variant="rounded" width={120} height={40} />
            <Skeleton variant="rounded" width={120} height={40} />
            <Skeleton variant="rounded" width={140} height={40} />
            <Skeleton variant="rounded" width={40} height={40} />
          </Box>
        </Paper>
        {renderSkeletons()}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ pt: 6, pl: 3, pr: 3, pb: 6, background: '#f5f7fa', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} 
              color="text.primary" display="flex" alignItems="center" gap={1}>
              <PersonIcon sx={{ color: theme.palette.primary.main }} />
              Team Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor team performance, activities & progress ({filteredMembers.length} members)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center',
          mb: 3, flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
            {filters.project !== 'all' && (
              <Button
                variant="outlined"
                startIcon={<ViewKanbanIcon />}
                onClick={() => navigate(`/projects/${filters.project}/board`)}
                sx={{
                  height: 48,
                  px: 2.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(67,97,238,0.15)',
                  },
                  textTransform: 'none',
                  transition: 'all 0.2s',
                  width: isMobile ? "100%" : "auto",
                }}
              >
                View Board
              </Button>
            )}
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} 
              disabled={loading} sx={{ ...sharedButtonStyles,
              background: `linear-gradient(135deg, ${theme.palette.primary.light}40 0%, 
                ${theme.palette.primary.main}20 100%)`,
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}30`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.light}60 0%, 
                  ${theme.palette.primary.main}40 100%)`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-1px)',
                borderColor: theme.palette.primary.main,
              },
              '&:active': { transform: 'translateY(0)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
            }}>
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </Box>
        </Box>

        <Paper elevation={0} sx={{ width: '100%', p: { xs: 1.5, sm: 2 }, mb: 3, 
          borderRadius: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
          overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center',
            justifyContent: 'space-between', gap: { xs: 1, sm: 1.5 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: '100%' }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center',
              minWidth: { xs: '100%', sm: 'auto' },
              flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
              {!searchOpen ? (
                <IconButton onClick={() => setSearchOpen(true)} size="medium" sx={{
                  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                  width: 40, height: 40, '&:hover': {
                    bgcolor: 'primary.main', color: 'primary.contrastText',
                    borderColor: 'primary.main'
                  }, transition: 'all 0.2s'
                }}>
                  <SearchIcon />
                </IconButton>
              ) : (
                <TextField size="small" inputRef={searchInputRef}
                  placeholder="Search team members..." value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small"
                          onClick={() => filters.search ? 
                            setFilters(prev => ({ ...prev, search: '' })) : setSearchOpen(false)}
                          sx={{ p: 0.5 }}>
                          <CloseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2, bgcolor: 'background.paper',
                      '& fieldset': { borderColor: 'divider' },
                      '&:hover fieldset': { borderColor: 'primary.light' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                    }
                  }}
                  fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 } }}
                />
              )}
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, 
              alignItems: 'center', flexWrap: 'wrap', ml: { sm: 'auto' } }}>
              <Autocomplete size="small" options={roleOptions}
                value={filters.role === 'all' ? null : filters.role}
                onChange={(e, newValue) => setFilters(prev => ({ ...prev, role: newValue || 'all' }))}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Role" size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper',
                      height: 40, minWidth: 120 } }} 
                  />
                )}
                sx={{ minWidth: 120 }}
              />
              <Autocomplete size="small" options={['all', 'active', 'inactive']}
                value={filters.status === 'all' ? null : filters.status}
                onChange={(e, newValue) => setFilters(prev => ({ ...prev, status: newValue || 'all' }))}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Status" size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper',
                      height: 40, minWidth: 120 } }} 
                  />
                )}
                sx={{ minWidth: 120 }}
              />
              <Autocomplete size="small" options={projectOptions}
                getOptionLabel={(opt) => opt.name || ''}
                value={projectOptions.find(p => p?.id == filters.project) || null}
                onChange={(e, newValue) => {
                  const newProjectId = newValue?.id ?? 'all';
                  setFilters(prev => ({ ...prev, project: newProjectId }));
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Project" size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper',
                      height: 40, minWidth: 140 } }} 
                  />
                )}
                sx={{ minWidth: 140 }}
              />
              <Tooltip title="Clear all filters">
                <IconButton onClick={resetFilters} size="medium" sx={{
                  bgcolor: (filters.search || filters.role !== 'all' || 
                    filters.status !== 'all' || filters.project !== 'all') ? 
                    'primary.light' : 'grey.200',
                  color: (filters.search || filters.role !== 'all' || 
                    filters.status !== 'all' || filters.project !== 'all') ? 
                    'primary.contrastText' : 'text.secondary',
                  width: 40, height: 40, '&:hover': {
                    bgcolor: 'primary.main', color: 'primary.contrastText'
                  }, transition: 'all 0.2s'
                }}>
                  <FilterIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: { xs: 'auto', sm: 'auto' } }}>
              <Button size="small" variant="outlined" startIcon={<FilterIcon />}
                onClick={() => setFiltersOpen(!filtersOpen)} sx={{
                  borderRadius: 2, height: 40, textTransform: 'none', minWidth: 'auto', px: 2
                }}>
                {filtersOpen ? 'Hide Filters' : 'Filters'}
              </Button>
            </Box>
          </Box>

          <Collapse in={filtersOpen} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
              <Autocomplete size="small" options={roleOptions} value={filters.role || 'all'}
                onChange={(e, newValue) => setFilters(prev => ({ ...prev, role: newValue || 'all' }))}
                renderInput={(params) => <TextField {...params} label="Role" size="small" />}
                fullWidth
              />
              <Autocomplete size="small" options={['all', 'active', 'inactive']}
                value={filters.status || 'all'}
                onChange={(e, newValue) => setFilters(prev => ({ ...prev, status: newValue || 'all' }))}
                renderInput={(params) => <TextField {...params} label="Status" size="small" />}
                fullWidth
              />
              <Autocomplete size="small" options={projectOptions}
                getOptionLabel={(opt) => opt.name || ''}
                value={projectOptions.find(p => p.id == filters.project) || null}
                onChange={(e, newValue) => setFilters(prev => ({ ...prev, project: newValue?.id || 'all' }))}
                renderInput={(params) => <TextField {...params} label="Project" size="small" />}
                fullWidth
              />
              <Button size="small" variant="contained" onClick={resetFilters} fullWidth
                sx={{ borderRadius: 2, height: 40 }}>
                Clear All Filters
              </Button>
            </Box>
          </Collapse>
        </Paper>

        {renderFilterChips().length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {renderFilterChips().map((chip, i) => (
              <Chip key={i} label={chip} onDelete={() => {
                if (chip.startsWith('Search:')) setFilters(prev => ({ ...prev, search: '' }));
                else if (chip.startsWith('Role:')) setFilters(prev => ({ ...prev, role: 'all' }));
                else if (chip.startsWith('Status:')) setFilters(prev => ({ ...prev, status: 'all' }));
                else if (chip.startsWith('Project:')) setFilters(prev => ({ ...prev, project: 'all' }));
              }} size="small" />
            ))}
          </Box>
        )}

        {filteredMembers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No team members found</Typography>
            <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            {filteredMembers.map((member) => {
              const userId = member.user_id;
              const stats = memberStats[userId] || calculateMemberStats(member);
              const kpis = memberKpis[userId] || [];
              
              return (
                <MemberCard
                  key={member.user_id}
                  member={member}
                  stats={stats}
                  kpis={kpis}
                  isExpanded={expandedMember === member.user_id}
                  expandedMember={expandedMember}
                  toggleExpand={toggleExpand}
                  handleViewDetails={handleViewDetails}
                  getMemberTasks={() => getMemberTasks(userId)}
                  getMemberSprints={() => getMemberSprints(userId)}
                  getMemberProjects={() => getMemberProjects(userId)}
                  getRoleColor={getRoleColor}
                  getAvatarUrl={getAvatarUrl}
                  navigate={navigate}
                />
              );
            })}
          </Grid>
        )}
      </Box>

      <Dialog open={detailDialogOpen} onClose={handleCloseDetails} maxWidth="md" 
        fullWidth fullScreen={isMobile}>
        {selectedMember && (
          <>
            <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={getAvatarUrl(selectedMember.user?.avatar)} sx={{
                    width: 56, height: 56, bgcolor: getRoleColor(selectedMember.role?.name)
                  }}>
                    {!selectedMember.user?.avatar ? 
                      selectedMember.user?.name?.charAt(0)?.toUpperCase() : null}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedMember.user?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedMember.role?.name} • {selectedMember.user?.email}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetails}><CloseIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} 
                sx={{ mb: 2 }}>
                <Tab label="Overview" />
                <Tab label="Projects" />
                <Tab label="Tasks" />
                <Tab label="KPIs" />
              </Tabs>
              
              {/* ✅ ADD TAB CONTENT HERE */}
              {selectedTab === 0 && selectedMember && (
                <Box>
                  <Typography variant="h6" gutterBottom>Member Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{selectedMember.user?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedMember.user?.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Role</Typography>
                      <Typography variant="body1">{selectedMember.role?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip label={selectedMember.is_active ? 'Active' : 'Inactive'} 
                        color={selectedMember.is_active ? 'success' : 'error'} size="small" />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {selectedTab === 1 && selectedMember && (
                <Box>
                  <Typography variant="h6" gutterBottom>Assigned Projects</Typography>
                  <List>
                    {getMemberProjects(selectedMember.user_id).map(project => (
                      <ListItem key={project.id} divider>
                        <ListItemText 
                          primary={project.name} 
                          secondary={`Status: ${project.status?.name || 'N/A'}`}
                        />
                      </ListItem>
                    ))}
                    {getMemberProjects(selectedMember.user_id).length === 0 && (
                      <Typography color="text.secondary">No projects assigned</Typography>
                    )}
                  </List>
                </Box>
              )}
              
              {selectedTab === 2 && selectedMember && (
                <Box>
                  <Typography variant="h6" gutterBottom>Assigned Tasks</Typography>
                  <List>
                    {getMemberTasks(selectedMember.user_id).slice(0, 10).map(task => (
                      <ListItem key={task.id} divider>
                        <ListItemText 
                          primary={task.title} 
                          secondary={`Status: ${task.status?.name || 'N/A'} | Priority: ${task.priority}`}
                        />
                      </ListItem>
                    ))}
                    {getMemberTasks(selectedMember.user_id).length === 0 && (
                      <Typography color="text.secondary">No tasks assigned</Typography>
                    )}
                  </List>
                </Box>
              )}
              
              {selectedTab === 3 && selectedMember && (
                <Box>
                  <Typography variant="h6" gutterBottom>KPIs</Typography>
                  <List>
                    {(memberKpis[selectedMember.user_id] || []).map((kpi, index) => (
                      <ListItem key={index} divider>
                        <ListItemText 
                          primary={kpi.title || kpi.name || 'KPI'} 
                          secondary={`Status: ${kpi.status} | Target: ${kpi.target} | Actual: ${kpi.actual}`}
                        />
                      </ListItem>
                    ))}
                    {(memberKpis[selectedMember.user_id] || []).length === 0 && (
                      <Typography color="text.secondary">No KPIs found</Typography>
                    )}
                  </List>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDetails} color="inherit">Close</Button>
              <Button variant="contained" onClick={() => navigate(`/profile`)}>
                Edit Profile
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default TeamDetailsPage;